import React, { useEffect, useMemo, useRef, useState } from "react";
import { get as commsGet } from "../../../service/comms/commsApi";
import { getAnalyticsCoaches } from "../../../service/comms/oversightAnalyticsService";
import { resolvedId } from "../../../utils/oversightFormat";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";
import { FilterChip } from "../../../common/comms/Chip";
import { Drawer } from "../../../common/comms/Drawer";

const OPTIONS_LIMIT = 100;
const MAX_VISIBLE_CHIPS = 12;

/**
 * Controlled multi-select coach filter for the Rooms tab.
 *
 * The parent owns the selection (sourced from `?coach_id=` URL params) and
 * receives the next selected id array via `onChange` — this component holds
 * no selection state of its own, only the fetched option list and (while the
 * mobile drawer is open) a pending draft selection.
 *
 * Fetches `getAnalyticsCoaches({ limit: 100 })` once on mount and caches the
 * result in state. If that fetch fails, the option list silently falls back
 * to empty and only the currently-selected ids are rendered as chips (same
 * degraded behavior as before this component existed), so the filter stays
 * usable even when the coach list can't load.
 *
 * Renders a wrapped `FilterChip` row on `sm:` and up (an "All coaches" chip
 * that clears the selection, one chip per coach — capped at 12 with a
 * "+N more" chip opening the drawer for the rest) and, below `sm:`, a single
 * "Filter by coach" button that opens `Drawer` with a checkbox list. The
 * drawer selection is a local draft applied only on "Apply", so toggling
 * checkboxes doesn't refetch rooms per tap.
 *
 * @param {Object} props
 * @param {string[]} props.selectedIds - Currently selected coach ids (URL-sourced).
 * @param {(nextIds: string[]) => void} props.onChange - Called with the next selected id array.
 */
export function CoachMultiSelect({ selectedIds, onChange }) {
  const { getCoachName } = useCommsIdentity();

  const [options, setOptions] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const fetchedRef = useRef(false);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [pendingIds, setPendingIds] = useState(selectedIds);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    getAnalyticsCoaches(commsGet, { limit: OPTIONS_LIMIT })
      .then((payload) => {
        const list = Array.isArray(payload?.coaches) ? payload.coaches : [];
        const mapped = list
          .map((coach) => {
            const id = resolvedId(coach);
            if (id == null) return null;
            const name = coach?.coach_name || getCoachName(id) || `#${String(id).slice(-6)}`;
            return { id: String(id), name, isOnline: !!coach?.is_online_now };
          })
          .filter(Boolean);
        setOptions(mapped);
      })
      .catch(() => {
        setLoadFailed(true);
      });
    // Fetch exactly once on mount regardless of prop/identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const knownIds = useMemo(() => new Set(options.map((option) => option.id)), [options]);

  // Selected ids not present in the fetched options — either the fetch
  // failed, or a deep-linked id fell outside the top-100 options page.
  // Rendered as chips too so an active filter never "disappears".
  const fallbackChips = useMemo(
    () =>
      selectedIds
        .filter((id) => !knownIds.has(id))
        .map((id) => ({
          id,
          name: getCoachName(id) || `#${String(id).slice(-6)}`,
          isOnline: false,
        })),
    [selectedIds, knownIds, getCoachName]
  );

  const allCoaches = useMemo(
    () => [...options, ...fallbackChips],
    [options, fallbackChips]
  );

  const shownChips = allCoaches.slice(0, MAX_VISIBLE_CHIPS);
  const overflowCount = allCoaches.length - shownChips.length;

  const toggleSelected = (id) => {
    const next = selectedSet.has(id)
      ? selectedIds.filter((existing) => existing !== id)
      : [...selectedIds, id];
    onChange(next);
  };

  const handleClearAll = () => onChange([]);

  const openDrawer = () => {
    setPendingIds(selectedIds);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => setIsDrawerOpen(false);

  const togglePending = (id) => {
    setPendingIds((current) =>
      current.includes(id) ? current.filter((existing) => existing !== id) : [...current, id]
    );
  };

  const handleApply = () => {
    onChange(pendingIds);
    setIsDrawerOpen(false);
  };

  const handleDrawerClear = () => setPendingIds([]);

  return (
    <>
      <div className="hidden sm:flex flex-wrap items-center gap-2 mb-4">
        <FilterChip
          label="All coaches"
          active={selectedIds.length === 0}
          onClick={handleClearAll}
        />
        {shownChips.map((coach) => (
          <FilterChip
            key={coach.id}
            label={coach.name}
            active={selectedSet.has(coach.id)}
            onClick={() => toggleSelected(coach.id)}
          />
        ))}
        {overflowCount > 0 && (
          <FilterChip label={`+${overflowCount} more`} onClick={openDrawer} />
        )}
      </div>

      <div className="flex sm:hidden mb-4">
        <button
          type="button"
          onClick={openDrawer}
          className="min-h-[44px] px-4 text-sm font-medium text-gray-700 rounded-lg border border-gray-200 bg-white hover:bg-orange-50"
        >
          Filter by coach{selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
        </button>
      </div>

      <Drawer isOpen={isDrawerOpen} onClose={closeDrawer} title="Filter by coach" width={360}>
        <div className="flex flex-col gap-1">
          {allCoaches.length === 0 ? (
            <p className="text-sm text-gray-400 py-4">
              {loadFailed ? "Couldn't load coaches." : "No coaches found."}
            </p>
          ) : (
            allCoaches.map((coach) => (
              <label
                key={coach.id}
                className="flex items-center gap-3 min-h-[44px] px-1 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={pendingIds.includes(coach.id)}
                  onChange={() => togglePending(coach.id)}
                  className="h-4 w-4 rounded border-gray-300 text-orange-primary focus:ring-orange-primary/30"
                />
                <span
                  aria-hidden="true"
                  className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                    coach.isOnline ? "bg-success" : "bg-gray-400"
                  }`}
                />
                <span className="text-sm text-gray-700 truncate">{coach.name}</span>
              </label>
            ))
          )}
        </div>

        <div className="flex items-center justify-between gap-2 pt-4 mt-2 border-t border-gray-200">
          <button
            type="button"
            onClick={handleDrawerClear}
            className="min-h-[44px] inline-flex items-center px-2 text-sm font-medium text-gray-500 hover:text-gray-800"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="min-h-[44px] px-4 text-sm font-semibold text-white bg-orange-primary rounded-lg hover:opacity-90"
          >
            Apply
          </button>
        </div>
      </Drawer>
    </>
  );
}

export default CoachMultiSelect;
